"use client"

// components/v3/primitives.tsx — shared design primitives for v3 standalone
// pages (/brand-engineering, /about, /work-with-me, /cases/*).
//
// Extracted from app/prototype/hybrid/sections.tsx so the W1 pages share one
// visual language with the hybrid homepage. When the hybrid 轉正 (cutover),
// its sections should migrate onto these primitives (Phase 2 cleanup).
//
// Language rule (AGENT-CONTRACT red line #2): zh-TW primary, en secondary.

import { type ReactNode, useEffect, useRef, useState } from "react"
import { animate, motion, useInView, useReducedMotion } from "framer-motion"

// ── Dual-register token system（方向 C「雙域收斂」, D-record pending）─────────
// The site performs 從複雜到信任 as two visual registers:
//   PAPER  — the editorial register. Narrative, essays, testimony, CTA.
//            Warm paper, ink, 朱砂 vermilion. Trust lives here.
//   DARK   — the instrument register. Diagnostic, ledger, metrics, terminal.
//            Ink-black lacquer, gold readouts. Complexity is worked here.
// Rules: vermilion never appears in the dark room; gold never on paper.
// Contrast floor: all body/small text ≥ 4.5:1 against its register bg.

export const paper = {
  bg: "#F2ECDF",
  bgDeep: "#EAE2CE", // cards / alt sections on paper
  ink: "#1F1B14", // primary text
  inkSoft: "#4A443A", // secondary text
  inkFaint: "#6E6353", // mono labels, metadata (≥4.5:1 on bg)
  accent: "#A93A25", // 朱砂 vermilion — links, emphasis, seal moments
  hairline: "#D8CFBA",
  hairlineSoft: "#E2DAC6",
} as const

export const dark = {
  bg: "#12100C",
  panel: "#1A160F",
  border: "#2C2820",
  text: "#ECE4D6",
  soft: "#9A8F7E",
  faint: "#8F8473", // decorative small mono only
  accent: "#C9A227", // gold — instrument readouts, never on paper
  warn: "#D16A5B", // oxblood — low scores / alerts inside instruments
} as const

// Legacy aliases (hybrid hero + pre-refactor call sites)
export const GOLD = dark.accent
export const OXBLOOD = dark.warn
export const ZH_SERIF = "var(--proto-noto-serif-tc), var(--proto-fraunces), serif"

// ── Modular type scale ────────────────────────────────────────────────────────
// One system for every register. `cls` carries size/leading; `vs` carries the
// variable-font weight for ZH_SERIF headings (never faux-bold, never italic on
// CJK). CJK body/caption leading stays ≥1.8 (AGENT-CONTRACT typography rule).
export const type = {
  display: { cls: "text-[2.6rem] leading-[1.25] md:text-[4.8rem] md:leading-[1.18]", vs: "'wght' 700" },
  h1: { cls: "text-4xl leading-[1.2] md:text-[3.6rem] md:leading-[1.15]", vs: "'wght' 640" },
  h2: { cls: "text-3xl leading-[1.3] md:text-[2.6rem] md:leading-[1.25]", vs: "'wght' 580" },
  h3: { cls: "text-xl leading-[1.5] md:text-2xl", vs: "'wght' 600" },
  lede: { cls: "text-xl leading-[1.85] md:text-[1.55rem] md:leading-[1.8]", vs: "'wght' 460" },
  body: { cls: "text-[15px] leading-[1.95]" },
  caption: { cls: "text-[13px] leading-[1.8]" },
  label: { cls: "text-[11px] tracking-[0.22em] uppercase" },
} as const

// ── Motion primitives ─────────────────────────────────────────────────────────

export function FadeUp({
  children,
  delay = 0,
  className,
  as = "div",
}: {
  children: ReactNode
  delay?: number
  className?: string
  as?: "div" | "li" | "section" | "header" | "p"
}) {
  const reduce = useReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: "-12% 0px -12% 0px" })
  const MotionTag = motion[as] as typeof motion.div
  return (
    <MotionTag
      ref={ref}
      className={className}
      initial={reduce ? false : { opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </MotionTag>
  )
}

// Animated metric counter — extracts the leading numeric run and counts to it,
// preserving any sign/suffix (e.g. "+45%", "−30%", "4.8/5", "Top").
export function Counter({ value, className }: { value: string; className?: string }) {
  const reduce = useReducedMotion()
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: "-20% 0px" })
  const [display, setDisplay] = useState(value)

  const match = value.match(/^([+−-]?)(\d+(?:\.\d+)?)(.*)$/)

  useEffect(() => {
    if (!inView || reduce || !match) {
      if (match) setDisplay(value)
      return
    }
    const [, sign, num, suffix] = match
    const target = parseFloat(num)
    const decimals = num.includes(".") ? num.split(".")[1].length : 0
    const controls = animate(0, target, {
      duration: 1.4,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(`${sign}${v.toFixed(decimals)}${suffix}`),
    })
    return () => controls.stop()
  }, [inView, reduce, value, match])

  return (
    <span ref={ref} className={className}>
      {match ? display : value}
    </span>
  )
}

// ChapterMark — 三幕劇章節記號（書卷式編章，CJK-first 的敘事骨架）
export function ChapterMark({
  act,
  zh,
  en,
  register = "paper",
}: {
  act: string
  zh: string
  en: string
  register?: "paper" | "dark"
}) {
  const c =
    register === "dark"
      ? { rule: dark.border, act: dark.accent, zh: dark.text, en: dark.faint }
      : { rule: paper.hairline, act: paper.accent, zh: paper.ink, en: paper.inkFaint }
  return (
    <FadeUp>
      <div className="mx-auto flex max-w-6xl items-center gap-5 px-6 pt-24 md:px-10 md:pt-32">
        <span
          className="text-[12px] tracking-[0.3em]"
          style={{ fontFamily: "var(--proto-mono)", color: c.act }}
        >
          {act}
        </span>
        <span
          lang="zh-Hant"
          className="text-[14px] tracking-[0.24em]"
          style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 600", color: c.zh }}
        >
          {zh}
        </span>
        <span className="h-px flex-1" style={{ background: c.rule }} aria-hidden />
        <span
          className="text-[10.5px] uppercase tracking-[0.24em]"
          style={{ fontFamily: "var(--proto-mono)", color: c.en }}
        >
          {en}
        </span>
      </div>
    </FadeUp>
  )
}

// StoryThread — v1 golden thread 的昇華：左緣一條敘事進度線，
// 顏色沿三幕劇換域（暗房＝金、紙域＝朱砂）。reduced-motion 時隱藏。
export function StoryThread() {
  const reduce = useReducedMotion()
  const [progress, setProgress] = useState(0)
  useEffect(() => {
    if (reduce) return
    let raf = 0
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const h = document.documentElement
        const max = h.scrollHeight - h.clientHeight
        setProgress(max > 0 ? h.scrollTop / max : 0)
      })
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      cancelAnimationFrame(raf)
    }
  }, [reduce])
  if (reduce) return null
  return (
    <div
      className="pointer-events-none fixed bottom-6 left-5 top-24 z-30 hidden w-px md:block"
      style={{ background: "rgba(110,99,83,0.18)" }}
      aria-hidden
    >
      <div
        className="w-full origin-top"
        style={{
          height: "100%",
          transform: `scaleY(${progress})`,
          background: `linear-gradient(to bottom, ${dark.accent} 0%, ${dark.accent} 18%, ${paper.accent} 30%, ${paper.accent} 55%, ${dark.accent} 63%, ${dark.accent} 72%, ${paper.accent} 84%, ${paper.accent} 100%)`,
        }}
      />
    </div>
  )
}

export function Eyebrow({
  zh,
  en,
  register = "paper",
}: {
  zh: string
  en?: string
  register?: "paper" | "dark"
}) {
  const c =
    register === "dark"
      ? { text: dark.soft, tick: dark.accent, en: dark.faint }
      : { text: paper.inkFaint, tick: paper.accent, en: paper.inkFaint }
  return (
    <span
      className="inline-flex items-center gap-3 text-[0.72rem] tracking-[0.28em]"
      style={{ fontFamily: "var(--proto-mono)", color: c.text }}
    >
      <span className="h-px w-8" style={{ background: c.tick }} />
      {zh}
      {en && <span className="uppercase opacity-80" style={{ color: c.en }}>{en}</span>}
    </span>
  )
}
